<template>
  <nav :class="b({ show, fadeIn })">
    <div
      :class="b('back-button-container', { isVisible: !!backButtonText })"
      :style="`top: ${verticalAnchorPosition}px`"
    >
      <button
        v-if="backButtonText"
        :class="b('back-button')"
        type="button"
        @click="back"
      >
        <e-icon-sprite icon="i-chevron-2--left" />
        {{ backButtonText }}
      </button>
    </div>

    <div
      v-for="(item, index) in navigationStack"
      :key="item.id"
      :class="b('level', getLevelBemAttributes(index))"
      :style="`top: ${verticalAnchorPosition}px`"
    >
      <e-scrollable>
        <div :class="b('inner')">
          <ul
            v-if="item.pages.length"
            :class="b('list')"
          >
            <li
              v-for="page in item.pages"
              :key="page.id"
              :class="b('item')"
            >
              <a
                :href="page.uri"
                :class="b('link', getLinkBemAttributes(page))"
                :target="page.target"
                :title="page.title"
                :role="!page.uri && 'button'"
                :rel="page.target === '_blank' ? 'noopener noreferrer' : null"
                @click="onClick($event, index + 1, page)"
              >
                {{ page.label }}
              </a>
            </li>
          </ul>
        </div>
        <c-user-navigation
          v-if="index === 0"
          :visible="open"
        />
      </e-scrollable>
    </div>
  </nav>
</template>

<script>
  import useSessionStore from '@/stores/session';
  import cUserNavigation from '@/components/c-user-navigation.vue';
  import eIconSprite from '@/elements/e-icon-sprite.vue';
  import eScrollable from '@/elements/e-scrollable.vue';

  const TRANSITION_DURATION = 600;

  /**
   * Renders the main navigation for the application.
   */
  export default {
    name: 'c-main-navigation-mobile',

    components: {
      eIconSprite,
      cUserNavigation,
      eScrollable,
    },
    // mixins: [],

    props: {
      /**
       * Open state.
       */
      open: {
        type: Boolean,
        default: false,
      },
    },
    setup() {
      return {
        sessionStore: useSessionStore(),
      };
    },
    data() {
      return {
        show: false,

        /**
         * @type {Boolean} Navigation is faded out flag.
         */
        fadeIn: false,

        /**
         * @type {Array.<Object>} Stores the currently active navigation stack.
         */
        navigationStack: [],

        /**
         * @type {Number} Vertical anchor position for positioning the flyout.
         */
        verticalAnchorPosition: 0,

        /**
         * @type {Boolean} True if user recently navigated.
         */
        hasNavigated: false,

        /**
         * @type {Object} Stores transition states for navigation levels.
         */
        transitionStates: {
          level0: false,
          level1: false,
          level2: false,
          level3: false,
          level4: false,
        },

        /**
         * @type {Array} Holds the breadcrumb list
         */
        breadcrumb: [],
      };
    },

    computed: {
      /**
       * Gets the back button text.
       *
       * @returns {String}
       */
      backButtonText() {
        if (!this.breadcrumb.length) {
          return null;
        }

        return this.breadcrumb.length === 1
          ? this.$t('c-main-navigation-mobile.backToOverview')
          : this.$t('c-main-navigation-mobile.backButtonTitle', {
              label: this.breadcrumb.at(-2),
            });
      },
    },
    watch: {
      /**
       * Observes open state and readjusts vertical position.
       *
       * @param {Boolean} isOpen Open state.
       */
      open(isOpen) {
        if (isOpen) {
          this.setVerticalAnchorPosition();
          this.show = true;

          setTimeout(() => {
            document.body.classList.add('disable-bodyscroll'); // Using bodyscroll was not possible because of an issue with iOS.
            this.fadeIn = true;
          });
        } else {
          this.fadeIn = false;

          setTimeout(() => {
            document.body.classList.remove('disable-bodyscroll');
            this.show = false;
          }, TRANSITION_DURATION);

          if (this.hasNavigated) {
            this.navigationStack = this.navigationStack.slice(0, 1);
            this.breadcrumb = [];
            this.hasNavigated = false;
          }
        }
      },
    },

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    mounted() {
      this.setInitialNavigationStack(0, this.sessionStore.mainNavigation);
      this.setVerticalAnchorPosition();

      window.addEventListener('resizeend', this.setVerticalAnchorPosition);
    },
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    beforeUnmount() {
      window.removeEventListener('resizeend', this.setVerticalAnchorPosition);
    },
    // unmounted() {},

    methods: {
      /**
       * Sets initial navigation stack recursive.
       *
       * @param {Number} level - The given navigation level.
       * @param {Object} node - The given navigation node.
       */
      setInitialNavigationStack(level, node) {
        if (level > 4 || !node.pages.length) {
          return;
        }

        const activePage = node.pages.find((page) => page.active);

        if (level > 0) {
          this.breadcrumb.push(node.label);
        }

        this.navigationStack.push(node);
        this.transitionStates[`level${level}`] = true;

        if (activePage) {
          this.setInitialNavigationStack(level + 1, activePage);
        }
      },

      /**
       * Sets vertical anchor position based on header bottom position.
       */
      setVerticalAnchorPosition() {
        const { bottom } = document.querySelector('header')?.getBoundingClientRect() || {};

        this.verticalAnchorPosition = bottom || 0;
      },

      /**
       * Click event handler.
       *
       * @param {Event} event - DOM Event.
       * @param {Number} level - Navigation level.
       * @param {Object} node - Navigation node.
       */
      onClick(event, level, node) {
        if (!node.pages.length) {
          return;
        }

        event.preventDefault();
        this.hasNavigated = true;
        this.updateMainNavigationStack(level, node);
      },

      /**
       * Closes all open subnavigations.
       */
      back() {
        this.hasNavigated = true;
        this.transitionStates[`level${Object.keys(this.navigationStack).length - 1}`] = false;
        this.breadcrumb.pop();

        setTimeout(() => {
          this.navigationStack.pop();
        }, TRANSITION_DURATION);
      },

      /**
       * Updates navigation stack.
       *
       * @param {Number} level - The level of the given node.
       * @param {Object} node - The given navigation node.
       */
      updateMainNavigationStack(level, node) {
        this.navigationStack.push(node);
        this.transitionStates[`level${level}`] = false;
        this.breadcrumb.push(node.label);

        this.$nextTick(() => {
          this.transitionStates[`level${level}`] = true;
        });
      },

      /**
       * Checks if the given page should be active.
       *
       * @param {Object} page - A page definition.
       *
       * @returns {Boolean}
       */
      isPageActive(page) {
        return page.active && !page.pages.length && !this.hasNavigated;
      },

      /**
       * Returns an object of bem attributes for the navigation links.
       *
       * @param {Object} page - A page definition.
       *
       * @returns {Object}
       */
      getLinkBemAttributes(page) {
        return {
          isActive: this.isPageActive(page),
          isHighlighted: page.highlighted,
          linkStyle: page.linkStyle,
        };
      },

      /**
       * Returns an object of bem attributes for the navigation level element.
       *
       * @param {Number} index - Index of the navigationStack.
       *
       * @returns {Object}
       */
      getLevelBemAttributes(index) {
        return {
          level: index,
          isTransitioned: this.transitionStates[`level${index}`],
        };
      },
    },
    // render() {},
  };
</script>

<style lang="scss">
  @use 'sass:map';
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/functions';
  @use '@/setup/scss/extends' as *;

  .c-main-navigation-mobile {
    $this: &;
    $c-main-navigation-mobile: map.get(variables.$z-index, 'navigation');

    display: none;
    opacity: 0;
    transition: opacity variables.$transition-duration--600 ease;

    &--show {
      display: block;
    }

    &--fade-in {
      opacity: 1;
    }

    &__list {
      @extend %list-reset;
    }

    &__link {
      @extend %link--dark-blue;

      outline: none;
      cursor: pointer;
    }

    &__level {
      position: fixed;
      right: 0;
      bottom: 0;
      left: 100%;
      z-index: $c-main-navigation-mobile;
      padding-top: functions.rem(115px);
      padding-bottom: functions.rem(variables.$spacing--40);
      background-color: variables.$color-grayscale--930;
      transition: all variables.$transition-duration--600 ease;
    }

    &__inner {
      @extend %layout;

      padding-right: functions.rem(variables.$spacing--20);
    }

    &__level--level-0 {
      padding-top: functions.rem(variables.$spacing--40);
    }

    &__level--level-0,
    &__level--is-transitioned {
      left: 0;
    }

    &__level--level-0,
    &__level--level-1 {
      #{$this}__item {
        margin-bottom: functions.rem(variables.$spacing--20);
      }

      #{$this}__link {
        @extend %link--dark-blue;
        @include mixins.font(variables.$font-size--18, variables.$line-height--18, variables.$font-weight--semi-bold);

        font-family: variables.$font-family--header;
      }
    }

    &__level--level-2,
    &__level--level-3,
    &__level--level-4 {
      #{$this}__item {
        margin-bottom: functions.rem(variables.$spacing--25);
      }

      #{$this}__link {
        @extend %link--dark-blue;
        @include mixins.font(variables.$font-size--16);

        font-family: variables.$font-family--text;
      }
    }

    &__level &__link--is-active {
      color: variables.$color-primary--2;
    }

    &__item:last-child {
      margin-bottom: functions.rem(variables.$spacing--40);
    }

    &__back-button-container {
      @extend %layout;

      position: fixed;
      z-index: $c-main-navigation-mobile + 1;
      width: 100%;
      padding-top: functions.rem(32px);
      opacity: 0;
      transition: opacity variables.$transition-duration--600 ease;
      pointer-events: none;
    }

    &__back-button-container--is-visible {
      opacity: 1;
      pointer-events: all;
    }

    &__back-button {
      @extend %button-reset;
      @include mixins.font(variables.$font-size--14, variables.$line-height--20);

      display: flex;
      align-items: center;
      margin-bottom: functions.rem(variables.$spacing--50);
      margin-left: functions.rem(-4px); // Compensate icon whitespace to align.
      color: variables.$color-grayscale--100;

      .e-icon-sprite {
        margin: -0.1em functions.rem(12px) 0 0.2em;
        width: functions.rem(14px);
        height: functions.rem(14px);
      }
    }

    &__level--level-1 &__link--link-style-alternative {
      @extend %link--dark-blue;
      @include mixins.font(variables.$font-size--16, variables.$line-height--18, variables.$font-weight--regular);

      display: block;
      padding-top: functions.rem(variables.$spacing--15);
      font-family: variables.$font-family--text;
    }

    &__level--level-2 &__link--is-highlighted {
      font-weight: variables.$font-weight--semi-bold;
    }

    .e-scrollable {
      overflow-x: hidden; // Prevents horizontal scrollbar being shown during transitions.
    }

    .simplebar-vertical.simplebar-vertical {
      right: functions.rem(variables.$spacing--25);
    }

    .c-user-navigation {
      @extend %layout;

      padding-top: functions.rem(variables.$spacing--45);

      &::before {
        @include mixins.fullwidth();

        position: absolute;
        display: block;
        content: '';
        height: 1px;
        margin-top: functions.rem(-1 * variables.$spacing--45);
        background-color: variables.$color-grayscale--900;
      }
    }
  }
</style>
